import { useCallback, useEffect, useState } from "react";

/**
 * useComposerFormatting
 * Shared formatting active-state and exec handlers for composer editors.
 */
export default function useComposerFormatting(editorRef) {
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    strike: false,
    link: false,
    ordered: false,
    bulleted: false,
    quote: false,
    code: false,
  });

  const recomputeFormatState = useCallback(() => {
    try {
      const editor = editorRef.current;
      const root = editor?.getElement?.();
      if (typeof window === 'undefined' || !root) return;
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) {
        setFormatState((prev) => (prev.bold || prev.italic || prev.strike || prev.link || prev.ordered || prev.bulleted || prev.quote || prev.code) ? ({ bold: false, italic: false, strike: false, link: false, ordered: false, bulleted: false, quote: false, code: false }) : prev);
        return;
      }
      const range = sel.getRangeAt(0);
      if (!root.contains(range.commonAncestorContainer)) {
        setFormatState((prev) => (prev.bold || prev.italic || prev.strike || prev.link || prev.ordered || prev.bulleted || prev.quote || prev.code) ? ({ bold: false, italic: false, strike: false, link: false, ordered: false, bulleted: false, quote: false, code: false }) : prev);
        return;
      }
      let node = range.commonAncestorContainer;
      if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
      const hasAncestorTag = (names) => {
        const set = new Set(names.map((n) => n.toLowerCase()));
        let n = node;
        while (n && n !== root && n.nodeType === Node.ELEMENT_NODE) {
          const tag = n.tagName?.toLowerCase?.();
          if (tag && set.has(tag)) return true;
          n = n.parentNode;
        }
        return false;
      };
      const next = {
        bold: hasAncestorTag(['b', 'strong']),
        italic: hasAncestorTag(['i', 'em']),
        strike: hasAncestorTag(['s', 'strike']),
        link: hasAncestorTag(['a']),
        ordered: hasAncestorTag(['ol']),
        bulleted: hasAncestorTag(['ul']),
        quote: hasAncestorTag(['blockquote']),
        code: hasAncestorTag(['code', 'pre']),
      };
      setFormatState((prev) => {
        for (const k of Object.keys(next)) { if (next[k] !== prev[k]) return next; }
        return prev;
      });
    } catch (_) {}
  }, [editorRef]);

  useEffect(() => {
    const handler = () => { try { recomputeFormatState(); } catch (_) {} };
    document.addEventListener('selectionchange', handler);
    return () => { document.removeEventListener('selectionchange', handler); };
  }, [recomputeFormatState]);

  const exec = useCallback((cmd) => editorRef.current?.exec(cmd), [editorRef]);
  const applyWrapFormatting = useCallback(({ kind }) => { exec(kind); }, [exec]);
  const applyCodeFormatting = useCallback(() => { exec('code'); try { recomputeFormatState(); } catch(_) {} }, [exec, recomputeFormatState]);
  const applyQuoteFormatting = useCallback(() => { exec('quote'); }, [exec]);
  const applyListFormatting = useCallback(({ ordered }) => { exec(ordered ? 'ol' : 'ul'); }, [exec]);
  const applyLinkFormatting = useCallback(() => { exec('link'); }, [exec]);

  return {
    formatState,
    recomputeFormatState,
    applyWrapFormatting,
    applyCodeFormatting,
    applyQuoteFormatting,
    applyListFormatting,
    applyLinkFormatting,
  };
}


